import { useEffect, useState } from "react";
import { Alert, Button, FileButton, Group, Image, Stack, Text } from "@mantine/core";

import type { BrandingImageInfo, UploadedImage } from "@/api/branding";
import {
  IMAGE_MAX_EDGE_PX,
  SLOT_MAX_BYTES,
  checkImage,
  fileBytes,
  formatBytes,
  type ImageRefusal,
  type ImageSlot,
} from "@/branding/images";
import { t, type MessageKey } from "@/i18n";

/**
 * One image slot of a lab's branding: the logo, the stamp, the master signature or a
 * doctor's own signature (contract 1.2, 4.10).
 *
 * The picked file is checked here first, with the server's own rules, so a lab is told an
 * SVG or a 2 MB photo is refused before it is sent. Then it is uploaded. **An upload takes
 * effect only when the branding is saved** naming its hash, so a picked image the lab does
 * not save changes nothing a patient sees.
 */

const REFUSAL_TEXT: Record<ImageRefusal, MessageKey> = {
  image_type_refused: "branding.image_type_refused",
  image_too_large: "branding.image_too_large",
  image_dimensions: "branding.image_dimensions",
};

interface ImageFieldProps {
  label: string;
  slot: ImageSlot;
  /** The hash this slot carries now, own or inherited, or null. */
  sha256: string | null;
  /** Set when the image is the parent's, not this organisation's own. */
  inheritedFrom: string | null;
  info: BrandingImageInfo | undefined;
  upload: (file: Blob) => Promise<UploadedImage>;
  onUploaded: (image: UploadedImage) => void;
  /** Absent when there is nothing of this organisation's own to remove. */
  onRemove?: () => void;
  error?: string | null;
  testId: string;
}

export function ImageField({
  label,
  slot,
  sha256,
  inheritedFrom,
  info,
  upload,
  onUploaded,
  onRemove,
  error,
  testId,
}: ImageFieldProps): JSX.Element {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  async function picked(file: File | null): Promise<void> {
    setProblem(null);
    if (file === null) return;
    const bytes = await fileBytes(file);
    const check = checkImage(bytes, slot);
    if (!check.ok) {
      setProblem(t(REFUSAL_TEXT[check.refusal]));
      return;
    }
    setBusy(true);
    try {
      const uploaded = await upload(file);
      onUploaded(uploaded);
      if (typeof URL.createObjectURL === "function") {
        setPreview(URL.createObjectURL(file));
      }
    } catch (err) {
      setProblem(err instanceof Error ? err.message : t("branding.upload_failed"));
    } finally {
      setBusy(false);
    }
  }

  const cap = SLOT_MAX_BYTES[slot];
  return (
    <Stack gap={4} data-testid={testId}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <Text size="xs" c="dimmed">
        {t("branding.image_limits")
          .replace("{cap}", formatBytes(cap))
          .replace("{edge}", String(IMAGE_MAX_EDGE_PX))}
      </Text>
      {sha256 !== null ? (
        <Group gap="sm">
          {preview !== null && <Image src={preview} alt={label} h={40} w="auto" fit="contain" />}
          <Text size="sm" data-testid={`${testId}-size`}>
            {info !== undefined
              ? `${formatBytes(info.byte_size)} / ${formatBytes(cap)}, ${info.width_px} x ${info.height_px} px`
              : t("branding.image_size_unknown")}
          </Text>
          {inheritedFrom !== null && (
            <Text size="xs" c="dimmed">
              {t("branding.inherited_from")} {inheritedFrom}
            </Text>
          )}
        </Group>
      ) : (
        <Text size="sm" c="dimmed">
          {t("branding.image_none")}
        </Text>
      )}
      <Group gap="xs">
        <FileButton onChange={(f) => void picked(f)} accept="image/png,image/jpeg">
          {(props) => (
            <Button
              {...props}
              size="xs"
              variant="light"
              loading={busy}
              data-testid={`${testId}-pick`}
            >
              {t("branding.image_choose")}
            </Button>
          )}
        </FileButton>
        {onRemove !== undefined && (
          <Button size="xs" variant="subtle" color="gray" onClick={onRemove}>
            {t("branding.image_remove")}
          </Button>
        )}
      </Group>
      {(problem ?? error) && (
        <Alert color="red" p="xs" data-testid={`${testId}-error`}>
          {problem ?? error}
        </Alert>
      )}
    </Stack>
  );
}
